/**
 * `PUT /triagem/{ticketId}/classificacao` (`TP-038`, `D5`) — a escolha fechada de tipo de solicitação e de
 * operadora de um ticket.
 *
 * Mesma regra de `confirmarCampo`: `requisitar` traduz qualquer `!ok` em `ErroDaApi` com `.status`, e nenhum
 * status vira sucesso silencioso. Quem chama distingue `400` (fora da lista), `404` (ticket que a sessão não
 * alcança), `409` (já conferido por outra pessoa) e `401` (sessão expirada).
 */

import type { ClassificacaoDaTriagemCorrigida, CorrigirClassificacaoRequest } from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { ErroDeContratoDeTriagem, mapearClassificacao } from '../../../shared/triagem/mapeadores';
import type { ClasseDoTipo, ClassificacaoDaTriagem } from '../../../shared/triagem/tipos';

/** A escolha da pessoa. `lembrarTexto`: passar a entender o texto de operadora lido como a operadora escolhida. */
export interface EscolhaDeClassificacao {
  readonly classeDoTipo: ClasseDoTipo;
  readonly operadoraId: string;
  readonly lembrarTexto: boolean;
}

/** O que ficou gravado, para a tela atualizar sem recarregar. */
export interface ClassificacaoCorrigida {
  readonly ticketId: string;
  readonly classificacao: ClassificacaoDaTriagem;
}

export function corrigirClassificacao(
  ticketId: string,
  escolha: EscolhaDeClassificacao,
  sinal?: AbortSignal,
): Promise<ClassificacaoCorrigida> {
  const corpo: CorrigirClassificacaoRequest = {
    requestTypeClass: escolha.classeDoTipo,
    payerId: escolha.operadoraId,
    rememberPayerText: escolha.lembrarTexto,
  };
  return requisitar<ClassificacaoDaTriagemCorrigida>(`/triagem/${encodeURIComponent(ticketId)}/classificacao`, {
    method: 'PUT',
    corpo,
    sinal,
  }).then((resposta) => {
    const classificacao = mapearClassificacao(resposta.classification);
    if (classificacao === null) throw new ErroDeContratoDeTriagem('classification', 'veio vazia');
    return { ticketId: resposta.ticketId, classificacao };
  });
}
